import { useContext, useMemo, useState } from 'react';
import { ApiContext, errorText } from '../../../lib/api';
import { cansInBlocks, minRunSeconds, shortestRun, templateSize } from '../../../lib/s3/arenaGrid';
import {
  isNotAvailable,
  previewArenaTemplate,
  type ArenaPreviewResponse,
} from '../../../lib/s3Api';
import type { ArenaTemplateDef, VehicleDef } from '../../../lib/s3/types';
import { inputCls, smallBtn } from '../fields';
import { useContentBundle } from '../refs';

const n = (v: number | null | undefined) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/**
 * The template, top down (lurerun.html "Admin: arena templates page"): blocks, wreck and fuel-can slots (a can inside a block in red),
 * the objective with its grab ring, the start, the exit, the horde spawn shares, the reinforcement edges, and the shortest legal run
 * line from start through the objective to the exit. The line is computed here on the nav grid; "Roll on the server" asks ArenaGenerator
 * (S3-ARS) for a seed's layout and its own run line, which then replaces it. y = 0 is the top (north) edge.
 */
export function ArenaPreview({ template }: { template: ArenaTemplateDef }) {
  const b = useContentBundle();
  const api = useContext(ApiContext);
  const vehicles = ((b.vehicles ?? []) as (VehicleDef | null)[]).filter(
    (v): v is VehicleDef => !!v,
  );
  const [vehicleId, setVehicleId] = useState('');
  const [seed, setSeed] = useState(1);
  const [horde, setHorde] = useState(150);
  const [busy, setBusy] = useState(false);
  const [server, setServer] = useState<ArenaPreviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const rules = (b.arenaRules ?? {}) as Parameters<typeof minRunSeconds>[2];
  const grab = b.arenaRules?.grab ?? {};
  const grabR = n(grab.radiusM ?? 8) || 8;
  const run = useMemo(() => shortestRun(template, grabR), [template, grabR]);
  const badCans = useMemo(() => new Set(cansInBlocks(template)), [template]);
  const [w, h] = templateSize(template);
  const vehicle = vehicles.find((v) => v.id === vehicleId) ?? vehicles[0];
  const minRun = vehicle
    ? minRunSeconds(
        run,
        n(vehicle.topSpeedMps),
        rules,
        template.objective?.kind ?? 'supply_truck',
        n(vehicle.cargoKg),
      )
    : null;

  const roll = async () => {
    if (!api) return;
    setBusy(true);
    setError(null);
    try {
      setServer(
        await previewArenaTemplate(api.client, {
          template,
          vehicleId: vehicle?.id,
          horde,
          seed,
        }),
      );
    } catch (e) {
      setServer(null);
      const text = errorText(e);
      setError(
        isNotAvailable(text)
          ? `Not available yet: the template preview is S3-ARS's. (${text})`
          : text,
      );
    } finally {
      setBusy(false);
    }
  };

  if (w <= 0 || h <= 0)
    return (
      <p className="mb-3 text-sm text-blam">Give the template a size (sizeM) to preview it.</p>
    );
  const line = server?.line.points.length ? server.line.points : run.points;
  const pad = 6;
  const edge = (side: string) => {
    const s = 3;
    switch (side) {
      case 'north':
        return <rect key={side} x={0} y={-s} width={w} height={s} className="fill-blam" />;
      case 'south':
        return <rect key={side} x={0} y={h} width={w} height={s} className="fill-blam" />;
      case 'west':
        return <rect key={side} x={-s} y={0} width={s} height={h} className="fill-blam" />;
      case 'east':
        return <rect key={side} x={w} y={0} width={s} height={h} className="fill-blam" />;
      default:
        return null;
    }
  };
  const obj = template.objective?.at;
  const ex = template.exit;
  return (
    <fieldset className="mb-4 rounded border-2 border-ink bg-white p-3">
      <legend className="px-1 text-sm font-bold">Top-down preview</legend>
      <svg
        viewBox={`${-pad} ${-pad} ${w + pad * 2} ${h + pad * 2}`}
        className="w-full border border-newsprint bg-newsprint/40"
        role="img"
        aria-label={`Arena ${template.id ?? ''} top-down preview`}
      >
        <rect
          x={0}
          y={0}
          width={w}
          height={h}
          className="fill-white stroke-ink"
          strokeWidth={0.6}
        />
        {(template.reinforceFrom ?? []).map((s) => edge(s))}
        {(template.spawn ?? []).map((s, i) =>
          s?.around ? (
            <g key={`sp${i}`}>
              <circle
                cx={n(s.around.x)}
                cy={n(s.around.y)}
                r={n(s.radiusM)}
                className="fill-blam/10 stroke-blam"
                strokeDasharray="3 2"
                strokeWidth={0.6}
              />
              <text
                x={n(s.around.x) + n(s.radiusM)}
                y={n(s.around.y)}
                fontSize={5}
                className="fill-blam"
              >
                {Math.round(n(s.share) * 100)}%
              </text>
            </g>
          ) : null,
        )}
        {(template.blocks ?? []).map((r, i) =>
          r ? (
            <g key={`b${i}`}>
              <rect x={n(r.x)} y={n(r.y)} width={n(r.w)} height={n(r.h)} className="fill-ink/70" />
              <text x={n(r.x) + 2} y={n(r.y) + 7} fontSize={6} className="fill-newsprint">
                {r.kind}
              </text>
            </g>
          ) : null,
        )}
        {ex ? (
          <rect
            x={n(ex.x)}
            y={n(ex.y)}
            width={n(ex.w)}
            height={n(ex.h)}
            className="fill-benday/60 stroke-ink"
            strokeWidth={0.6}
          >
            <title>exit</title>
          </rect>
        ) : null}
        {(template.wreckSlots ?? []).map((p, i) =>
          p ? (
            <text
              key={`w${i}`}
              x={n(p.x)}
              y={n(p.y)}
              fontSize={7}
              textAnchor="middle"
              dominantBaseline="middle"
              className="fill-bruise"
            >
              ✕
            </text>
          ) : null,
        )}
        {(template.fuelCanSlots ?? []).map((p, i) =>
          p ? (
            <rect
              key={`c${i}`}
              x={n(p.x) - 2}
              y={n(p.y) - 2}
              width={4}
              height={4}
              className={badCans.has(i) ? 'fill-blam stroke-ink' : 'fill-caption stroke-ink'}
              strokeWidth={0.4}
            >
              <title>
                {badCans.has(i) ? `fuel can ${i + 1}: inside a block` : `fuel can ${i + 1}`}
              </title>
            </rect>
          ) : null,
        )}
        {line.length > 1 && (
          <polyline
            points={line.map((p) => `${p.x},${p.y}`).join(' ')}
            fill="none"
            className="stroke-benday"
            strokeWidth={1.4}
            data-testid="run-line"
          />
        )}
        {(server?.layout.wrecks ?? []).map((p, i) => (
          <circle key={`sw${i}`} cx={p.x} cy={p.y} r={p.radiusM || 2.2} className="fill-bruise" />
        ))}
        {(server?.layout.fuelCans ?? []).map((p, i) => (
          <circle
            key={`sc${i}`}
            cx={p.x}
            cy={p.y}
            r={2}
            className="fill-caption stroke-ink"
            strokeWidth={0.5}
          />
        ))}
        {(server?.layout.spawns ?? []).map((p, i) => (
          <circle
            key={`sz${i}`}
            cx={p.x}
            cy={p.y}
            r={0.8}
            className={p.prize ? 'fill-blam' : 'fill-bruise'}
          />
        ))}
        {obj ? (
          <g>
            <circle
              cx={n(obj.x)}
              cy={n(obj.y)}
              r={grabR}
              className="fill-caption/30 stroke-ink"
              strokeDasharray="2 2"
              strokeWidth={0.5}
            />
            <rect
              x={n(obj.x) - 3}
              y={n(obj.y) - 2}
              width={6}
              height={4}
              className="fill-caption stroke-ink"
              strokeWidth={0.6}
            >
              <title>{template.objective?.kind ?? 'objective'}</title>
            </rect>
          </g>
        ) : null}
        {template.start ? (
          <circle
            cx={n(template.start.x)}
            cy={n(template.start.y)}
            r={3}
            className="fill-benday stroke-ink"
            strokeWidth={0.6}
          >
            <title>start</title>
          </circle>
        ) : null}
      </svg>
      <div className="mt-2 grid gap-1 text-sm">
        {run.problems.length ? (
          <ul className="text-blam" role="alert">
            {run.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : (
          <p>
            Shortest legal run: <b>{Math.round(run.totalM)} m</b> ({Math.round(run.toObjectiveM)} m
            to the {template.objective?.kind === 'survivor' ? 'pick-up' : 'truck'},{' '}
            {Math.round(run.totalM - run.toObjectiveM)} m out; every wreck slot filled)
            {minRun != null && vehicle ? (
              <>
                {' '}
                · at least <b>{Math.round(minRun * 10) / 10} s</b> on the{' '}
                {vehicle.name || vehicle.id} (the min-run bound: top speed with the slack, plus a
                full grab)
              </>
            ) : null}
          </p>
        )}
        {badCans.size > 0 && (
          <p className="text-blam">
            Fuel can slot(s) {[...badCans].map((i) => i + 1).join(', ')} inside a block.
          </p>
        )}
        {server && (
          <p className="text-xs">
            Server (seed {seed}):{' '}
            {server.line.totalM != null
              ? `run ${Math.round(server.line.totalM)} m`
              : 'no legal run'}
            {server.vehicles
              .map((v) => ` · ${v.vehicleId} ≥ ${Math.round(v.minRunSeconds * 10) / 10} s`)
              .join('')}
            {server.errors?.length ? '' : ' · no errors'}
          </p>
        )}
        {server?.errors?.length ? (
          <ul className="text-xs text-blam">
            {server.errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        ) : null}
        {error && <p className="text-xs text-blam">{error}</p>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <label className="flex items-center gap-1">
          vehicle
          <select
            aria-label="Preview vehicle"
            className={`${inputCls} w-auto`}
            value={vehicle?.id ?? ''}
            onChange={(e) => setVehicleId(e.target.value)}
          >
            {vehicles.length === 0 && <option value="">(no vehicles)</option>}
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name || v.id}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          seed
          <input
            aria-label="Preview seed"
            type="number"
            className={`${inputCls} w-24`}
            value={seed}
            onChange={(e) => setSeed(Math.trunc(Number(e.target.value)) || 0)}
          />
        </label>
        <label className="flex items-center gap-1">
          horde
          <input
            aria-label="Preview horde"
            type="number"
            className={`${inputCls} w-20`}
            value={horde}
            onChange={(e) => setHorde(Math.trunc(Number(e.target.value)) || 0)}
          />
        </label>
        <button
          type="button"
          className={smallBtn}
          disabled={!api || busy}
          onClick={() => void roll()}
        >
          {busy ? 'Rolling…' : 'Roll on the server'}
        </button>
        {server && (
          <button type="button" className={smallBtn} onClick={() => setServer(null)}>
            Clear the server layout
          </button>
        )}
      </div>
    </fieldset>
  );
}
